import {
  TextInput, Stack, NumberInput, Button, LoadingOverlay, Title, type StackProps
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import useEnsisMutation from '../../hooks/useEnsisMutation';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import type { Organization } from '../../types/apiTypes';

interface CreateOrgFormValues {
  name: string
  accessDays: number | null
}

const validateOrgName = (orgName: string, existingOrgs: Organization[]) => {
  if (orgName.trim().length < 1) {
    return 'Name is required';
  } else if (
    existingOrgs.map((org) => org.name.toLowerCase()).includes(orgName.toLowerCase())
  ) {
    return 'Organization with this name already exists';
  }

  return null;
};

const CreateOrganizationForm: React.FC<StackProps> = (props: StackProps) => {
  const navigate = useNavigate();

  const { data: orgData, isLoading: orgDataLoading } = useEnsisQuery('/app/organizations');

  const form = useForm<CreateOrgFormValues>({
    initialValues: {
      name: '',
      accessDays: null
    },
    validate: {
      name: (value: string) => validateOrgName(value, orgData?.items ?? []),
      accessDays: (value: number | null) => ((value ?? 1) < 1 ? 'Access days must be at least 1' : null)
    }
  });

  const handleCreateOrgSuccess = useCallback((org: Organization) => {
    form.reset();
    navigate(`/org/${org.slug}`);
  }, [form.reset, navigate]);

  const createOrgMutation = useEnsisMutation(
    '/app/organizations',
    {
      requestType: 'post',
      successMessage: 'Organization Created',
      queryKeysToInvalidate: ['/app/organizations'],
      onSuccess: handleCreateOrgSuccess
    }
  );

  const handleSubmit = useCallback((values: CreateOrgFormValues) => {
    createOrgMutation.mutate({
      name: values.name,
      access_duration_days: values.accessDays ?? undefined
    });
  }, []);

  const loading = createOrgMutation.isPending || orgDataLoading;

  return (
      <Stack maw={500} {...props}>
        <LoadingOverlay visible={loading} />
        <Title order={6}>
          Create Organization
        </Title>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              required
              label='Organization Name'
              {...form.getInputProps('name')}
            />
            <NumberInput
              w={158}
              label='Access Duration (days)'
              min={1}
              allowDecimal={false}
              {...form.getInputProps('accessDays')}
            />
            <Button type='submit' disabled={loading}>
              Create
            </Button>
          </Stack>
        </form>
      </Stack>
  );
};

export default CreateOrganizationForm;
